import Ky from 'ky';

import { config } from '../config';

import type { KyInstance } from 'ky';
import type { SvpAsset, SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type { UnixTimestamp } from '@vgtv/api-client';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { Vendor } from '../types';

export interface AssetWatchtime {
  // in seconds
  time: number;
  assetId: SvpAsset['id'];
  updatedAt: UnixTimestamp;
  isWatched: boolean;
}

const getWatchtimeHistory =
  (client: KyInstance) =>
  async ({ userUuid, vendor }: { userUuid: string; vendor: string }) => {
    const response = await client
      .get(`${vendor}/watchtimes/${userUuid}/history`)
      .json<{ items: AssetWatchtime[] }>();

    return response.items;
  };

const getAssetWatchtimes =
  (client: KyInstance) =>
  async ({
    userUuid,
    vendor,
    assetIds,
  }: {
    userUuid: string;
    vendor: Vendor;
    assetIds: SvpAssetId[];
  }) => {
    if (!assetIds.length) {
      return [];
    }

    const response = await client
      .get(`${vendor}/watchtimes/${userUuid}`, {
        searchParams: { assetId: assetIds.join(',') },
      })
      .json<{ items: AssetWatchtime[] }>();

    return response.items;
  };

const getSeriesWatchtimes =
  (client: KyInstance) =>
  async ({
    userUuid,
    vendor,
    seriesIds,
  }: {
    userUuid: string;
    vendor: Vendor;
    seriesIds: SvpCategoryId[];
  }) => {
    return client
      .get(`${vendor}/watchtimes/${userUuid}/series`, {
        searchParams: { seriesId: seriesIds.join(',') },
      })
      .json<{
        items: { seriesId: SvpCategoryId; watchtime: AssetWatchtime }[];
      }>();
  };

const saveAssetWatchtime =
  (client: KyInstance) =>
  ({
    userUuid,
    vendor,
    data,
  }: {
    userUuid: string;
    vendor: Vendor;
    data: Omit<AssetWatchtime, 'isWatched'>;
  }): Promise<AssetWatchtime> =>
    client
      .put(`${vendor}/watchtimes/${userUuid}/${data.assetId}`, {
        json: {
          time: data.time,
          updatedAt: data.updatedAt,
        },
      })
      .json<AssetWatchtime>();

const createWatchtimeApi = (client: KyInstance) => {
  return {
    getWatchtimeHistory: getWatchtimeHistory(client),
    getAssetWatchtimes: getAssetWatchtimes(client),
    saveAssetWatchtime: saveAssetWatchtime(client),
    getSeriesWatchtimes: getSeriesWatchtimes(client),
  };
};

const watchtimeApi = createWatchtimeApi(
  Ky.create({
    prefixUrl: config.watchtimeApiHostname,
    credentials: 'include',
  })
);

export default watchtimeApi;
